import z from 'zod';
import { daysFromTodayUs, isUsCalendarDate } from '@/shared/infra/utils/calendarDate';
import { HIRE_DATE_MAX_DAYS_AHEAD } from '@/features/section-2/application/domain/entities/hire-date-window';

// Regex patterns
const nameRegex = /^(?=.*[A-Za-z\u00C0-\u017F])[A-Za-z\u00C0-\u017F''\-\s]+$/;
const ssnRegex = /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/;

export const invitationSchema = z.object({
  firstName: z.preprocess(
    (v) => (typeof v === 'string' ? v : (v ?? '')),
    z
      .string()
      .trim()
      .min(1, { message: 'First name is required' })
      .regex(nameRegex, { message: 'Please enter a valid first name' })
  ),

  lastName: z.preprocess(
    (v) => (typeof v === 'string' ? v : (v ?? '')),
    z
      .string()
      .trim()
      .min(1, { message: 'Last name is required' })
      .regex(nameRegex, { message: 'Please enter a valid last name' })
  ),

  email: z.preprocess(
    (v) => (typeof v === 'string' ? v : (v ?? '')),
    z
      .string()
      .trim()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Please enter a valid email address' })
  ),

  locationId: z.preprocess(
    (v) => (typeof v === 'string' ? v : (v ?? '')),
    z
      .string()
      .trim()
      .min(1, { message: 'Location is required' })
  ),

  ssn: z
    .string()
    .trim()
    .optional()
    .refine((v) => v === undefined || v === '' || ssnRegex.test(v), {
      message: 'Please enter a valid SSN (XXX-XX-XXXX)',
    }),

  /**
   * Optional hire date (VTK-1953). `InputDatePicker` stores MM/DD/YYYY, so that is what is
   * validated; the request mapper converts it to the calendar date the API takes.
   *
   * Checked as a real date rather than by regex alone — the masked input happily produces
   * 02/30/2026. Inviting ahead of a start date is normal; inviting for next year is a typo.
   */
  hireDate: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || isUsCalendarDate(v), {
      message: 'Please enter a valid hire date (MM/DD/YYYY)',
    })
    .refine((v) => !v || (daysFromTodayUs(v) ?? 0) <= HIRE_DATE_MAX_DAYS_AHEAD, {
      message: `Hire date cannot be more than ${HIRE_DATE_MAX_DAYS_AHEAD} days in the future`,
    }),
});

export type InvitationFormData = z.infer<typeof invitationSchema>;

export default invitationSchema;
